/**
 * App.tsx - Componente raíz de la SPA
 * Orquesta el flujo de pago completo
 */

import { FC, useReducer, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  SelectPaymentMethodHeader,
  CheckoutStepper,
  PaymentMethodCarousel,
  PaymentErrorBox,
  CVVInput,
  InstallmentSelector,
  AddressInfoBox,
  paymentMethodItemId,
  TogglePaymentCardsButton,
  SplitPaymentCardSection,
  SplitSectionDivider,
  CheckoutTotalFooter,
  SessionExpired,
  AddCardForm,
  GiftCardPromo,
  GiftCardPromoModal,
  ConfirmDeleteCardModal,
  CardStatusModal,
  PaymentSuccessPanel,
} from '../components';
import { PaymentMethodsSkeleton } from '../components/WebPayment/PaymentMethodsSkeleton';
import { useSplitPaymentValidation } from '../hooks/useSplitPaymentValidation';
import { isSplitReadyForPreInitiate } from '../application/splitPaymentReadiness';
import type { PaymentMethodItem } from '../components/PaymentMethodCarousel/types';
import type { InstallmentPlan } from '../components/InstallmentSelector';
import type { GiftCardItem, PaymentSelection, PaymentTotals } from '../domain/models';
import type { StepperStep } from '../components/CheckoutStepper/types';
import { translate } from '../i18n';
import { PaymentError, PaymentRequestError, PaymentTokenError } from '../domain/errors';
import type { NewCardData, PaymentOutcome, SavedCard } from '../domain/models';
import { addCard } from '../application/addCard';
import { paymentTokenErrorMessage } from '../application/addCardErrors';
import { isSessionCard, resolvePaymentTokens } from '../application/paymentTokens';
import type { ChargeInput } from '../application/paymentTokens';
import { mergeCards } from '../application/mergeCards';
import { assertGiftCardApplied, giftCardsFromCart, OH_GIFTCARD_PROVIDER } from '../application/giftCards';
import { isCardId, toPaymentMethods } from '../application/paymentMethods';
import { roundMoneyAmount } from '../utils/moneyAmount';
import { extractMercadoPagoRedirectUrl } from '../application/mercadoPagoRedirect';
import {
  mapPreviewResponse,
  mergeInstallmentPlansForPromoSync,
  resolveInstallmentsSelectionKey,
  runInstallmentPromoSync,
  runPreInitiateRouting,
  structuralChargesKey,
} from '../application/paymentPreviewSession';
import { computeInstallmentFinancingInterest } from '../application/installments';
import type { HostDiscountSummary } from '../application/discountSummary';
import {
  installmentPromoScenarioForMonths,
  parseInstallmentPromoPreview,
  type ParsedInstallmentPromoPreview,
} from '../application/installmentPromoPreview';
import { processPayment } from '../application/processPayment';
import type { CardCharge } from '../application/processPayment';
import { createCardDesignResolver } from '../infrastructure/cardDesignResolver';
import { createPaymentMiddlewareClient } from '../infrastructure/paymentMiddlewareClient';
import type { PaymentMiddlewareClient } from '../infrastructure/paymentMiddlewareClient';
import { PaymentMiddlewareClientProvider } from './PaymentMiddlewareClientContext';
import {
  createPreInitiateTotalSource,
  type CheckoutTotalSource,
} from '../infrastructure/checkoutTotalSource';
import {
  createMiddlewareSavedCardsSource,
  type SavedCardsSource,
} from '../infrastructure/savedCardsSource';
import { createPaywayTokenizer } from '../infrastructure/paywayTokenizer';
import { getDeviceUniqueIdentifier } from '../infrastructure/deviceIdentifier';
import { createSessionCardCache } from '../infrastructure/sessionCardCache';
import { readPaywayConfig } from '../config/paywayConfig';
import { readOrderPlaced, redirectToOrderPlaced } from './orderPlacedRoute';
import {
  HOST_REQUEST_PAYMENT_INIT_MESSAGE,
  listenForHostPaymentInit,
  paymentInitSessionKey,
  readPaymentInit,
} from './paymentInit';
import { readEmbedSource, readHostViewport, type HostViewport } from './embedSource';
import { isDesktop } from '../platform';
import { isMercadoPagoSubmitReady, type MercadoPagoPreInitiateStatus } from './mercadoPagoPreInitiate';
import { listenForHostSessionReset } from './sessionReset';
import {
  CHECKOUT_HOST_MESSAGE_SOURCE,
  PAYMENT_IFRAME_MESSAGE_SOURCE,
  postPaymentHostApproved,
  postPaymentHostInterest,
  postPaymentHostPreview,
  type PaymentHostRates,
  postPaymentHostToast,
  reportFrameHeight,
  requestHostMercadoPagoRedirect,
} from './reportFrameHeight';
import { usePaymentAnalytics } from './usePaymentAnalytics';
import { WebPayment } from '../components/WebPayment';

/** En web desktop la lista arranca sin tarjeta marcada. App y web mobile sí eligen la primera. */
function shouldSelectFirstCard(hostViewport: HostViewport | null): boolean {
  if (!isDesktop) return true;
  return hostViewport === 'mobile';
}

interface SplitPaymentAmounts {
  first?: number;
  second?: number;
}

type AppView = 'payment' | 'add-card';

type PreInitiateStatus = 'idle' | 'loading' | 'success' | 'error';

interface AppState {
  view: AppView;
  /** `true` hasta que terminó la carga inicial de tarjetas (el GET puede tardar varios segundos). */
  loadingMethods: boolean;
  /** `true` tras el primer `LOAD_PAYMENT_METHODS` exitoso (evita “Agregar tarjeta”/MP con error previo). */
  paymentMethodsReady: boolean;
  paymentMethods: PaymentMethodItem[];
  cardsById: Record<string, SavedCard>;
  installmentPlans: InstallmentPlan[];
  secondInstallmentPlans: InstallmentPlan[];
  total: number;
  /** `totals.total` del último `pre-initiate`: lo que backend valida en `initiate` (`total-amount-mismatch`). */
  backendTotal?: number;
  /** Desglose (`totals`) del último `pre-initiate`, para el resumen del footer. */
  backendTotals?: PaymentTotals;
  /** `discountSummary` del carrito simulado (nombres de promo VTEX). */
  backendDiscountSummary?: HostDiscountSummary;
  paymentSelection?: PaymentSelection;
  cvv?: string;
  selectedInstallmentId?: string;
  splitPaymentEnabled: boolean;
  firstCardId?: string;
  secondCardId?: string;
  amounts: SplitPaymentAmounts;
  secondCvv?: string;
  secondInstallmentId?: string;
  loading: boolean;
  error?: string;
  success: boolean;
  paymentResult?: PaymentOutcome;
  giftCardModalOpen: boolean;
  /** Gift cards aplicadas al carrito, tal como las devuelve backend. */
  giftCards: GiftCardItem[];
  /** Estado de `pre-initiate` para la selección actual de tarjeta(s). */
  preInitiateStatus: PreInitiateStatus;
  /** `installment-promo/sync` en vuelo: solo bloquea pagar; totales/descuentos vienen del pre-initiate / dual preview. */
  installmentPromoSyncPending: boolean;
  /** Falló el último `installment-promo/sync` (la grilla de routing sigue visible). */
  installmentPromoSyncFailed: boolean;
}

type AppAction =
  | { type: 'SET_VIEW'; payload: AppView }
  | {
      type: 'LOAD_PAYMENT_METHODS';
      payload: { items: PaymentMethodItem[]; cardsById: Record<string, SavedCard> };
      /** En web desktop la lista arranca sin tarjeta marcada. */
      selectFirstCard: boolean;
    }
  | { type: 'LOAD_INSTALLMENT_PLANS'; payload: { first: InstallmentPlan[]; second: InstallmentPlan[] } }
  | { type: 'LOAD_TOTAL'; payload: number }
  | { type: 'LOAD_BACKEND_TOTAL'; payload: number | undefined }
  | { type: 'LOAD_BACKEND_TOTALS'; payload: PaymentTotals | undefined }
  | { type: 'LOAD_BACKEND_DISCOUNT_SUMMARY'; payload: HostDiscountSummary | undefined }
  | { type: 'UPDATE_SELECTION'; payload: PaymentSelection }
  | { type: 'UPDATE_CVV'; payload: string }
  | { type: 'SELECT_INSTALLMENT'; payload: string }
  | { type: 'TOGGLE_SPLIT_PAYMENT' }
  | { type: 'SELECT_FIRST_CARD'; payload: string }
  | { type: 'SELECT_SECOND_CARD'; payload: string }
  | { type: 'UPDATE_AMOUNT'; card: 'first' | 'second'; payload: number | undefined }
  | { type: 'UPDATE_SECOND_CVV'; payload: string }
  | { type: 'SELECT_SECOND_INSTALLMENT'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_LOADING_METHODS'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_CARD_SELECTION' }
  | { type: 'SET_SUCCESS'; payload: PaymentOutcome }
  | { type: 'RESET' }
  | { type: 'OPEN_GIFT_CARD_MODAL' }
  | { type: 'CLOSE_GIFT_CARD_MODAL' }
  | { type: 'SET_GIFT_CARDS'; payload: GiftCardItem[] }
  | { type: 'SET_PRE_INITIATE_STATUS'; payload: PreInitiateStatus }
  | { type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING'; payload: boolean }
  | { type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED'; payload: boolean };

const initialState: AppState = {
  view: 'payment',
  loadingMethods: true,
  paymentMethodsReady: false,
  paymentMethods: [],
  cardsById: {},
  installmentPlans: [],
  secondInstallmentPlans: [],
  total: 0,
  splitPaymentEnabled: false,
  amounts: {},
  loading: false,
  success: false,
  giftCardModalOpen: false,
  giftCards: [],
  preInitiateStatus: 'idle',
  installmentPromoSyncPending: false,
  installmentPromoSyncFailed: false,
};

const cvvIsComplete = (cvv?: string): boolean => {
  const length = cvv?.length ?? 0;
  return length === 3 || length === 4;
};

/** Conserva la cuota elegida si el nuevo cálculo la sigue ofreciendo; si no, preselecciona "1 cuota". */
const nextInstallmentId = (
  plans: InstallmentPlan[],
  current: string | undefined,
): string | undefined =>
  plans.find((plan) => plan.id === current)?.id ?? plans.find((plan) => plan.months === 1)?.id;

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_VIEW':
      return { ...state, view: action.payload };
    case 'LOAD_PAYMENT_METHODS': {
      const methods = action.payload.items;
      // Los ids son posicionales: si la lista se reordena (p. ej. el GET ya trae la tarjeta recién agregada),
      // las selecciones se vuelven a buscar por tarjeta (token o BIN + últimos 4).
      const remapId = (oldId: string | undefined): string | undefined => {
        const previous = oldId ? state.cardsById[oldId] : undefined;
        if (!previous) {
          return oldId;
        }
        return Object.entries(action.payload.cardsById).find(
          ([, card]) =>
            card.token === previous.token ||
            (card.bin === previous.bin && card.lastFourDigits === previous.lastFourDigits),
        )?.[0];
      };
      const currentId = remapId(state.paymentSelection?.selectedPaymentMethodId);
      const currentStillThere =
        !!currentId && methods.some((item) => paymentMethodItemId(item) === currentId);
      const firstCardId = action.selectFirstCard
        ? methods.find((item) => item.kind === 'card')?.id
        : undefined;
      const selectedPaymentMethodId = currentStillThere ? currentId : firstCardId;
      return {
        ...state,
        loadingMethods: false,
        paymentMethodsReady: true,
        paymentMethods: methods,
        cardsById: action.payload.cardsById,
        paymentSelection: selectedPaymentMethodId ? { selectedPaymentMethodId } : undefined,
        firstCardId: remapId(state.firstCardId),
        secondCardId: remapId(state.secondCardId),
      };
    }
    case 'LOAD_INSTALLMENT_PLANS':
      return {
        ...state,
        installmentPlans: action.payload.first,
        secondInstallmentPlans: action.payload.second,
        selectedInstallmentId: nextInstallmentId(action.payload.first, state.selectedInstallmentId),
        secondInstallmentId: nextInstallmentId(action.payload.second, state.secondInstallmentId),
      };
    case 'LOAD_TOTAL':
      return { ...state, total: action.payload };
    case 'LOAD_BACKEND_TOTAL':
      return { ...state, backendTotal: action.payload };
    case 'LOAD_BACKEND_TOTALS':
      return { ...state, backendTotals: action.payload };
    case 'LOAD_BACKEND_DISCOUNT_SUMMARY':
      return { ...state, backendDiscountSummary: action.payload };
    case 'UPDATE_SELECTION':
      return { ...state, paymentSelection: action.payload, error: undefined };
    case 'UPDATE_CVV':
      return { ...state, cvv: action.payload };
    case 'SELECT_INSTALLMENT':
      return { ...state, selectedInstallmentId: action.payload };
    case 'TOGGLE_SPLIT_PAYMENT':
      return {
        ...state,
        splitPaymentEnabled: !state.splitPaymentEnabled,
        firstCardId: !state.splitPaymentEnabled
          ? state.paymentSelection?.selectedPaymentMethodId
          : undefined,
        secondCardId: undefined,
        amounts: {},
        secondCvv: undefined,
        secondInstallmentId: undefined,
      };
    case 'SELECT_FIRST_CARD':
      // La misma tarjeta no puede ir en las dos posiciones: si era la segunda, esa queda sin elegir.
      return {
        ...state,
        firstCardId: action.payload,
        ...(state.secondCardId === action.payload
          ? { secondCardId: undefined, secondCvv: undefined, secondInstallmentId: undefined }
          : {}),
      };
    case 'SELECT_SECOND_CARD':
      return action.payload === state.firstCardId
        ? state
        : { ...state, secondCardId: action.payload };
    case 'UPDATE_AMOUNT':
      return { ...state, amounts: { ...state.amounts, [action.card]: action.payload } };
    case 'UPDATE_SECOND_CVV':
      return { ...state, secondCvv: action.payload };
    case 'SELECT_SECOND_INSTALLMENT':
      return { ...state, secondInstallmentId: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LOADING_METHODS':
      return { ...state, loadingMethods: action.payload };
    case 'SET_ERROR':
      return {
        ...state,
        loadingMethods: !state.paymentMethodsReady,
        error: action.payload,
      };
    case 'CLEAR_CARD_SELECTION':
      // Los ids de tarjeta son posicionales: al cambiar la lista se limpian las selecciones.
      return {
        ...state,
        paymentSelection: undefined,
        firstCardId: undefined,
        secondCardId: undefined,
        cvv: undefined,
        secondCvv: undefined,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: undefined };
    case 'SET_SUCCESS':
      return { ...state, success: true, paymentResult: action.payload };
    case 'RESET':
      return initialState;
    case 'OPEN_GIFT_CARD_MODAL':
      return { ...state, giftCardModalOpen: true };
    case 'CLOSE_GIFT_CARD_MODAL':
      return { ...state, giftCardModalOpen: false };
    case 'SET_GIFT_CARDS':
      return { ...state, giftCards: action.payload };
    case 'SET_PRE_INITIATE_STATUS':
      return { ...state, preInitiateStatus: action.payload };
    case 'SET_INSTALLMENT_PROMO_SYNC_PENDING':
      return { ...state, installmentPromoSyncPending: action.payload };
    case 'SET_INSTALLMENT_PROMO_SYNC_FAILED':
      return { ...state, installmentPromoSyncFailed: action.payload };
    default:
      return state;
  }
};

/** En dev, los datos de `devFixtures` (Galicia / Nación / Supervielle) solo aplican al iframe standalone. */
const isEmbeddedInHost = (): boolean => {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
};

/** Espera a que el host (checkout web) envíe las credenciales por postMessage antes de dar la sesión por inválida. */
const HOST_INIT_GRACE_MS = 3000;

/** Tras redirigir a Mercado Pago desde la app, tiempo antes de liberar el botón si la página sigue viva. */
const MERCADOPAGO_REDIRECT_RELEASE_MS = 3000;

const App: FC = () => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [preInitiateRetryNonce, setPreInitiateRetryNonce] = useState(0);
  const [installmentPromoSyncRetryNonce, setInstallmentPromoSyncRetryNonce] = useState(0);
  const analytics = usePaymentAnalytics({
    paymentMethods: state.paymentMethods as PaymentMethodItem[],
    selectedMethodId: state.paymentSelection?.selectedPaymentMethodId,
  });
  const clientRef = useRef<PaymentMiddlewareClient | null>(null);
  const [paymentClient, setPaymentClient] = useState<PaymentMiddlewareClient | null>(null);
  const withPaymentClient = (node: ReactNode) => (
    <PaymentMiddlewareClientProvider client={paymentClient}>{node}</PaymentMiddlewareClientProvider>
  );
  const cardServicesRef = useRef<Parameters<typeof addCard>[0] | null>(null);
  /** Tarjetas del GET (sin las nuevas de la sesión, que viven en la caché). */
  const remoteCardsRef = useRef<SavedCard[]>([]);
  const mercadoPagoEnabledRef = useRef(false);
  const preInitiateRequestRef = useRef(0);
  const preInitiateAbortRef = useRef<AbortController | null>(null);
  const installmentPromoSyncRequestRef = useRef(0);
  const installmentPromoSyncAbortRef = useRef<AbortController | null>(null);
  const abortInFlightPreInitiate = useCallback(() => {
    preInitiateAbortRef.current?.abort();
    preInitiateAbortRef.current = null;
  }, []);
  const abortInFlightInstallmentPromoSync = useCallback(() => {
    installmentPromoSyncAbortRef.current?.abort();
    installmentPromoSyncAbortRef.current = null;
  }, []);
  const lastPreInitiatedStructuralKeyRef = useRef('');
  const lastSyncedInstallmentsKeyRef = useRef('');
  const routingInstallmentPlansRef = useRef<{ first: InstallmentPlan[]; second: InstallmentPlan[] }>({
    first: [],
    second: [],
  });
  /** 1 cuota de la última simulación completa (pre-initiate / sync), para interés de financiación en el host. */
  const financingReferenceOneCuotaRef = useRef<{ first?: number; second?: number }>({});
  const installmentPromoPreviewRef = useRef<ParsedInstallmentPromoPreview | null>(null);
  const selectedInstallmentIdRef = useRef(state.selectedInstallmentId);
  selectedInstallmentIdRef.current = state.selectedInstallmentId;
  const secondInstallmentIdRef = useRef(state.secondInstallmentId);
  secondInstallmentIdRef.current = state.secondInstallmentId;
  const [mercadoPagoPreInitiate, setMercadoPagoPreInitiate] = useState<MercadoPagoPreInitiateStatus>('idle');
  const mercadoPagoPreInitiateStatusRef = useRef<MercadoPagoPreInitiateStatus>('idle');
  const mercadoPagoPreInitiateRequestRef = useRef(0);
  const mercadoPagoPreInitiateAbortRef = useRef<AbortController | null>(null);
  const abortInFlightMercadoPagoPreInitiate = useCallback(() => {
    mercadoPagoPreInitiateAbortRef.current?.abort();
    mercadoPagoPreInitiateAbortRef.current = null;
  }, []);
  const paymentMethodsLoadRef = useRef(0);
  /** Misma sesión que el último bootstrap: repetir `payment-init` solo actualiza el total. */
  const bootstrappedSessionKeyRef = useRef<string | null>(null);
  const submittingRef = useRef(false);
  const [hostViewport, setHostViewport] = useState<HostViewport | null>(() =>
    readHostViewport(window.location.search),
  );

  useEffect(() => {
    if (!isDesktop) return;
    return reportFrameHeight();
  }, []);

  // Si el iframe fue redirigido a la ruta de pago aprobado, solo se muestra esa pantalla.
  const orderPlaced = useMemo(() => readOrderPlaced(window.location), []);

  // Abierto sin las credenciales del host (p. ej. alguien con la URL del iframe): pantalla de sesión inválida.
  const [missingSession, setMissingSession] = useState(false);

  // Inicializar con las credenciales que inyecta el host (`window.__CARREFOUR_PAYMENT_INIT__`).
  // Tarjetas: GET payment/card + tarjetas nuevas de la sesión. Total: `totals` de pre-initiate (respaldo: el de la app).
  useEffect(() => {
    if (orderPlaced) {
      return;
    }

    const load = async (
      client: PaymentMiddlewareClient,
      cardsSource: SavedCardsSource,
      totalSource: CheckoutTotalSource,
      checkoutSessionId?: string,
    ) => {
      const loadId = ++paymentMethodsLoadRef.current;
      dispatch({ type: 'SET_LOADING_METHODS', payload: true });

      clientRef.current = client;
      setPaymentClient(client);
      const cardCache = createSessionCardCache();
      // Tarjetas de otra sesión de checkout (p. ej. el `session-reset` no llegó): se vacían antes de usarlas.
      if (checkoutSessionId) cardCache.bindSession(checkoutSessionId);
      // La config de Payway se lee recién al tokenizar: si falta, falla solo el alta de tarjeta.
      cardServicesRef.current = {
        tokenizer: createPaywayTokenizer(() => readPaywayConfig(), {
          getFraudContext: () => ({ deviceUniqueIdentifier: getDeviceUniqueIdentifier() }),
        }),
        cardCache,
      };
      let remoteCards: SavedCard[] = [];
      let mercadoPagoEnabled = false;
      try {
        ({ cards: remoteCards, mercadoPagoEnabled } = await cardsSource.getPaymentOptions());
      } catch {
        if (loadId === paymentMethodsLoadRef.current) {
          showGenericError();
        }
        return;
      }
      if (loadId !== paymentMethodsLoadRef.current) {
        return;
      }
      remoteCardsRef.current = remoteCards;
      mercadoPagoEnabledRef.current = mercadoPagoEnabled;
      dispatch({
        type: 'LOAD_PAYMENT_METHODS',
        payload: toPaymentMethods(mergeCards(remoteCards, cardCache.list()), createCardDesignResolver(), mercadoPagoEnabledRef.current),
        selectFirstCard: shouldSelectFirstCard(readHostViewport(window.location.search)),
      });

      let totalAmount = 0;
      try {
        totalAmount = await totalSource.getTotal();
      } catch {
        // El host puede mandar `totalAmount` en un `payment-init` posterior (carrito aún hidratando).
      }
      if (loadId !== paymentMethodsLoadRef.current) {
        return;
      }
      if (totalAmount > 0) {
        dispatch({ type: 'LOAD_TOTAL', payload: totalAmount });
      }
    };
    const showGenericError = () =>
      dispatch({ type: 'SET_ERROR', payload: translate('paymentsUI.errorBox.genericError') });

    let missingSessionTimer: number | undefined;
    const cancelMissingSessionWatch = () => {
      if (missingSessionTimer !== undefined) {
        window.clearTimeout(missingSessionTimer);
        missingSessionTimer = undefined;
      }
    };

    const bootstrap = (init: ReturnType<typeof readPaymentInit>) => {
      if (!init) return false;
      const sessionKey = paymentInitSessionKey(init);
      if (bootstrappedSessionKeyRef.current === sessionKey) {
        cancelMissingSessionWatch();
        setMissingSession(false);
        if (init.totalAmount && init.totalAmount > 0) {
          dispatch({ type: 'LOAD_TOTAL', payload: init.totalAmount });
        }
        return true;
      }
      cancelMissingSessionWatch();
      setMissingSession(false);
      bootstrappedSessionKeyRef.current = sessionKey;
      const client = createPaymentMiddlewareClient(init);
      load(
        client,
        createMiddlewareSavedCardsSource(client),
        createPreInitiateTotalSource(client, init.totalAmount),
        'checkoutSessionId' in init && typeof init.checkoutSessionId === 'string'
          ? init.checkoutSessionId
          : undefined,
      ).catch(showGenericError);
      return true;
    };

    const init = readPaymentInit();
    if (init && import.meta.env.DEV && import.meta.env.VITE_DEV_TEST_MODE === 'true') {
      if (isEmbeddedInHost() && init.mode === 'proxy') {
        console.warn(
          '[payment-embed] VITE_DEV_TEST_MODE está activo: se ignoran checkout-proxy y payment-init del host. ' +
            'En checkout-web (5175) desactivá VITE_DEV_TEST_MODE y usá solo proxy; este modo es para iframe en 5173 o WebView con Bearer en .env.local.',
        );
      }
      // Solo desarrollo: credenciales inyectadas por la app, vía proxy de Vite y con initiate bloqueado.
      import('./devFixtures')
        .then(({ createDevSetup }) => {
          const { client, cardsSource, totalSource } = createDevSetup(init);
          return load(client, cardsSource, totalSource);
        })
        .catch(showGenericError);
      return;
    }

    const stopListening = listenForHostPaymentInit((hostInit) => {
      setMissingSession(false);
      bootstrap(hostInit);
    });

    if (bootstrap(init)) {
      return () => stopListening();
    }

    if (isEmbeddedInHost()) {
      window.parent.postMessage(
        { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: HOST_REQUEST_PAYMENT_INIT_MESSAGE },
        '*',
      );
      // El host (checkout web / WebView) puede tardar un poco en enviar `payment-init`.
      missingSessionTimer = window.setTimeout(() => {
        if (!bootstrappedSessionKeyRef.current) {
          setMissingSession(true);
        }
      }, HOST_INIT_GRACE_MS);
      return () => {
        cancelMissingSessionWatch();
        stopListening();
      };
    }

    // UI del iframe en el puerto propio (5173): mocks o backend real sin host.
    // En 5175 `/payment-embed/` sin parent es acceso directo → misma UX que producción.
    if (import.meta.env.DEV && window.location.port === '5173') {
      import('./devFixtures')
        .then(({ createDevSetup }) => {
          const { client, cardsSource, totalSource } = createDevSetup();
          return load(client, cardsSource, totalSource);
        })
        .catch(showGenericError);
      return () => stopListening();
    }

    setMissingSession(true);
    return () => stopListening();
  }, [orderPlaced]);

  // El host avisa que la sesión se invalidó: se descartan las tarjetas de la sesión (token, BIN, últimos 4)
  // y el iframe queda en sesión inválida hasta un nuevo `payment-init`.
  useEffect(
    () =>
      listenForHostSessionReset(() => {
        (cardServicesRef.current?.cardCache ?? createSessionCardCache()).clear();
        remoteCardsRef.current = [];
        mercadoPagoEnabledRef.current = false;
        // Invalida cargas en vuelo y permite re-inicializar con el próximo `payment-init`.
        paymentMethodsLoadRef.current += 1;
        bootstrappedSessionKeyRef.current = null;
        clientRef.current = null;
        cardServicesRef.current = null;
        setPaymentClient(null);
        dispatch({ type: 'RESET' });
        setMissingSession(true);
      }),
    [],
  );

  const handleCVVChange = useCallback((cvv: string) => {
    dispatch({ type: 'UPDATE_CVV', payload: cvv });
  }, []);

  // El CVV incompleto ya se avisa debajo del propio campo. No se copia a la caja de error general:
  // al aparecer con cada tecla, el scroll automático hacia el error movía la pantalla mientras se escribía.
  const handleCVVError = useCallback((error: string | undefined) => {
    if (!error) {
      dispatch({ type: 'CLEAR_ERROR' });
    }
  }, []);

  const handleToggleSplitPayment = useCallback(() => {
    if (!state.splitPaymentEnabled) analytics.onEnableSplit();
    dispatch({ type: 'TOGGLE_SPLIT_PAYMENT' });
  }, [analytics.onEnableSplit, state.splitPaymentEnabled]);

  const handleShowAddCardForm = useCallback(() => {
    dispatch({ type: 'SET_VIEW', payload: 'add-card' });
  }, []);

  const handleBackToPayment = useCallback(() => {
    dispatch({ type: 'SET_VIEW', payload: 'payment' });
  }, []);

  // Modal "Tarjeta agregada con éxito" sobre la pantalla de pago.
  const [cardAddedModal, setCardAddedModal] = useState(false);
  // Modal genérico cuando el pago no se pudo completar (mensaje opcional debajo del título).
  const [paymentErrorModal, setPaymentErrorModal] = useState<{ message: string } | null>(null);

  const handleDismissPaymentFailure = useCallback(() => {
    setPaymentErrorModal(null);
    dispatch({ type: 'CLEAR_ERROR' });
    dispatch({ type: 'SET_VIEW', payload: 'payment' });
  }, []);

  // Tarjeta nueva: tokeniza (SDK Payway) → la guarda en la caché de sesión (backend la persiste al
  // completarse la operación) → la suma a las guardadas y la selecciona.
  const splitRef = useRef({ enabled: false, firstCardId: undefined as string | undefined });
  splitRef.current = { enabled: state.splitPaymentEnabled, firstCardId: state.firstCardId };

  const handleSaveNewCard = useCallback(async (data: NewCardData) => {
    const services = cardServicesRef.current;
    if (!services) {
      throw new PaymentError('Payment services are not ready');
    }

    const added = await addCard(services, data);
    const model = toPaymentMethods(
      mergeCards(remoteCardsRef.current, services.cardCache.list()),
      createCardDesignResolver(), mercadoPagoEnabledRef.current,
    );
    dispatch({ type: 'LOAD_PAYMENT_METHODS', payload: model, selectFirstCard: false });

    // Si la tarjeta ya estaba guardada (mismo BIN y últimos 4), prevalece la guardada y se selecciona.
    const entries = Object.entries(model.cardsById);
    const addedId = (
      entries.find(([, card]) => card.token === added.token) ??
      entries.find(([, card]) => card.bin === added.bin && card.lastFourDigits === added.lastFourDigits)
    )?.[0];
    if (addedId) {
      const split = splitRef.current;
      if (split.enabled) {
        // Pago dividido: la tarjeta nueva ocupa la posición libre (la segunda, salvo que falte la primera).
        if (!split.firstCardId) {
          dispatch({ type: 'SELECT_FIRST_CARD', payload: addedId });
        } else if (addedId !== split.firstCardId) {
          dispatch({ type: 'SELECT_SECOND_CARD', payload: addedId });
        }
      } else {
        dispatch({ type: 'UPDATE_SELECTION', payload: { selectedPaymentMethodId: addedId } });
      }
    }
    dispatch({ type: 'SET_VIEW', payload: 'payment' });
    if (isDesktop) {
      postPaymentHostToast('success', translate('paymentsUI.cardStatusModal.successTitle'));
    } else {
      setCardAddedModal(true);
    }
  }, []);

  // Papelera: tarjeta guardada (GET) → DELETE en backend; tarjeta de la sesión → solo se quita de la sesión.
  // La papelera abre un modal de confirmación; recién al confirmar se elimina.
  const deletingRef = useRef(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; last4: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleAskDeleteCard = useCallback((item: PaymentMethodItem) => {
    if (item.kind === 'card') {
      setPendingDelete({ id: item.id, last4: item.last4 });
    }
  }, []);

  const handleAskDeleteCardById = useCallback(
    (id: string) => {
      const card = state.cardsById[id];
      if (card) {
        setPendingDelete({ id, last4: card.lastFourDigits });
      }
    },
    [state.cardsById],
  );

  const handleCancelDeleteCard = useCallback(() => setPendingDelete(null), []);

  const handleConfirmDeleteCard = useCallback(
    async () => {
      const services = cardServicesRef.current;
      const client = clientRef.current;
      const card = pendingDelete ? state.cardsById[pendingDelete.id] : undefined;
      if (!services || !client || !card || deletingRef.current) {
        setPendingDelete(null);
        return;
      }

      deletingRef.current = true;
      setDeleting(true);
      try {
        if (isSessionCard(card)) {
          services.cardCache.remove(card.token);
        } else {
          await client.deleteCard('PAYWAY', card.token);
          remoteCardsRef.current = remoteCardsRef.current.filter((remote) => remote.token !== card.token);
        }

        // Los ids del carrusel son posicionales: se vuelve a armar la lista y se conserva la selección.
        const selectedCard = state.cardsById[state.paymentSelection?.selectedPaymentMethodId ?? ''];
        const model = toPaymentMethods(
          mergeCards(remoteCardsRef.current, services.cardCache.list()),
          createCardDesignResolver(), mercadoPagoEnabledRef.current,
        );
        dispatch({ type: 'LOAD_PAYMENT_METHODS', payload: model, selectFirstCard: false });
        const entries = Object.entries(model.cardsById);
        const nextSelectedId =
          selectedCard && selectedCard.token !== card.token
            ? entries.find(([, candidate]) => candidate.token === selectedCard.token)?.[0]
            : shouldSelectFirstCard(hostViewport)
              ? entries[0]?.[0]
              : undefined;
        dispatch({ type: 'UPDATE_SELECTION', payload: { selectedPaymentMethodId: nextSelectedId } });
        dispatch({ type: 'CLEAR_ERROR' });
        if (isDesktop) {
          postPaymentHostToast(
            'success',
            translate('paymentsUI.errorBox.deleteCardSuccess', { last4: card.lastFourDigits }),
          );
        }
      } catch {
        const deleteError = translate('paymentsUI.errorBox.deleteCardError');
        if (isDesktop) {
          postPaymentHostToast('error', deleteError);
        } else {
          dispatch({ type: 'SET_ERROR', payload: deleteError });
        }
      } finally {
        deletingRef.current = false;
        setDeleting(false);
        setPendingDelete(null);
      }
    },
    [pendingDelete, state.cardsById, state.paymentSelection?.selectedPaymentMethodId, hostViewport],
  );

  const handleFirstAmountChange = useCallback((value: number | undefined) => {
    dispatch({ type: 'UPDATE_AMOUNT', card: 'first', payload: value });
  }, []);

  const handleSecondAmountChange = useCallback((value: number | undefined) => {
    dispatch({ type: 'UPDATE_AMOUNT', card: 'second', payload: value });
  }, []);

  const handleSecondCVVChange = useCallback((cvv: string) => {
    dispatch({ type: 'UPDATE_SECOND_CVV', payload: cvv });
  }, []);

  const handleSelectFirstCard = useCallback((item: PaymentMethodItem) => {
    const id = paymentMethodItemId(item);
    analytics.onUserSelect(id);
    dispatch({ type: 'SELECT_FIRST_CARD', payload: id });
  }, [analytics.onUserSelect]);

  const handleSelectSecondCard = useCallback((item: PaymentMethodItem) => {
    const id = paymentMethodItemId(item);
    analytics.onUserSelect(id);
    dispatch({ type: 'SELECT_SECOND_CARD', payload: id });
  }, [analytics.onUserSelect]);

  const handleOpenGiftCardModal = useCallback(() => {
    dispatch({ type: 'OPEN_GIFT_CARD_MODAL' });
  }, []);

  const handleCloseGiftCardModal = useCallback(() => {
    dispatch({ type: 'CLOSE_GIFT_CARD_MODAL' });
  }, []);

  // Gift cards (ENA-1638): `POST` / `DELETE /v2/order/carts/gift-cards` devuelven el carrito; la lista sale de
  // `discountSummary.giftCards`. Los errores los maneja el modal.
  const handleApplyGiftCard = useCallback(async (redemptionCode: string) => {
    const client = clientRef.current;
    if (!client) {
      throw new PaymentError('Payment services are not ready');
    }
    analytics.onGiftCodes([redemptionCode]);
    const cart = await client.applyGiftCard({
      redemptionCode,
      provider: OH_GIFTCARD_PROVIDER,
    });
    assertGiftCardApplied(cart, redemptionCode);
    dispatch({ type: 'SET_GIFT_CARDS', payload: giftCardsFromCart(cart) });
  }, [analytics.onGiftCodes]);

  const handleRemoveGiftCard = useCallback(async (giftCardId: string) => {
    const client = clientRef.current;
    if (!client) {
      throw new PaymentError('Payment services are not ready');
    }
    const cart = await client.removeGiftCard(giftCardId);
    dispatch({ type: 'SET_GIFT_CARDS', payload: giftCardsFromCart(cart) });
  }, []);

  // El error puede quedar fuera de pantalla (debajo de las cuotas): se lo lleva a la vista.
  useEffect(() => {
    if (state.error) {
      document
        .querySelector('[data-testid="payment-error"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [state.error]);

  const cardOptions = state.paymentMethods.filter((item) => item.kind === 'card');

  /** Solo las tarjetas guardadas piden CVV en cada pago. */
  const needsCvv = (card: SavedCard | undefined): boolean => card !== undefined && !isSessionCard(card);

  // Pago con 2 tarjetas (mobile): ¿ya se puede consultar `pre-initiate`? Mientras no, se indica qué completar.
  const splitReady = isSplitReadyForPreInitiate({
    firstCardId: state.firstCardId,
    secondCardId: state.secondCardId,
    firstAmount: state.amounts.first,
    secondAmount: state.amounts.second,
    total: state.total,
    firstCvv: state.cvv,
    secondCvv: state.secondCvv,
    firstNeedsCvv: needsCvv(state.firstCardId ? state.cardsById[state.firstCardId] : undefined),
    secondNeedsCvv: needsCvv(state.secondCardId ? state.cardsById[state.secondCardId] : undefined),
  });

  const splitPaymentValidation = useSplitPaymentValidation({
    total: state.total,
    amounts: state.splitPaymentEnabled
      ? [state.amounts.first, state.amounts.second]
      : [state.amounts.first ?? state.total],
  });

  // Tarjetas + monto que se enviarán al middleware (vacío mientras la selección esté incompleta).
  const charges = useMemo<CardCharge[]>(() => {
    if (!state.splitPaymentEnabled) {
      const selectedId = state.paymentSelection?.selectedPaymentMethodId;
      const card = isCardId(selectedId) ? state.cardsById[selectedId] : undefined;
      return card ? [{ card, amount: state.total }] : [];
    }

    const first = state.firstCardId ? state.cardsById[state.firstCardId] : undefined;
    const second = state.secondCardId ? state.cardsById[state.secondCardId] : undefined;
    const { first: firstAmount, second: secondAmount } = state.amounts;
    if (!first || !second || !splitPaymentValidation.isValid) {
      return [];
    }
    return [
      { card: first, amount: firstAmount ?? 0 },
      { card: second, amount: secondAmount ?? 0 },
    ];
  }, [
    state.splitPaymentEnabled,
    state.paymentSelection?.selectedPaymentMethodId,
    state.cardsById,
    state.total,
    state.firstCardId,
    state.secondCardId,
    state.amounts,
    splitPaymentValidation.isValid,
  ]);

  // Un instrumento por tarjeta elegida (con 1 tarjeta se envía 1, con 2 se envían 2). En pago dividido las
  // cuotas de la primera tarjeta se piden apenas se la elige (con el monto ingresado o, si falta, el total).
  // `slot` indica a qué tarjeta de la pantalla corresponde cada `payments[i]` de la respuesta.
  const { preCharges, splitIncomplete } = useMemo(() => {
    const installmentsFor = (slot: 'first' | 'second') => {
      const raw = slot === 'first' ? state.selectedInstallmentId : state.secondInstallmentId;
      const parsed = Number(raw);
      return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
    };
    if (!state.splitPaymentEnabled) {
      return {
        preCharges: charges.map((charge) => ({
          ...charge,
          slot: 'first' as const,
          installments: installmentsFor('first'),
        })),
        splitIncomplete: false,
      };
    }
    const first = state.firstCardId ? state.cardsById[state.firstCardId] : undefined;
    const second = state.secondCardId ? state.cardsById[state.secondCardId] : undefined;
    // Mobile: no se consulta `pre-initiate` hasta tener ambas tarjetas, sus montos (suma = total inicial) y sus CVV.
    // Así no se muestran cuotas ni totales basados en datos incompletos. Desktop conserva su flujo.
    if (!isDesktop && !isSplitReadyForPreInitiate({
      firstCardId: state.firstCardId,
      secondCardId: state.secondCardId,
      firstAmount: state.amounts.first,
      secondAmount: state.amounts.second,
      total: state.total,
      firstCvv: state.cvv,
      secondCvv: state.secondCvv,
      firstNeedsCvv: needsCvv(first),
      secondNeedsCvv: needsCvv(second),
    })) {
      return { preCharges: [], splitIncomplete: true };
    }
    if (first && second) {
      // Con las dos tarjetas hacen falta ambos montos y que sumen el total; hasta entonces se conservan las cuotas.
      return {
        preCharges: charges.map((charge, index) => ({
          ...charge,
          slot: index === 0 ? ('first' as const) : ('second' as const),
          installments: installmentsFor(index === 0 ? 'first' : 'second'),
        })),
        splitIncomplete: charges.length === 0,
      };
    }
    const only = first ?? second;
    const typed = first ? state.amounts.first : state.amounts.second;
    return {
      preCharges: only
        ? [
            {
              card: only,
              amount: typed !== undefined && typed > 0 ? typed : state.total,
              slot: first ? ('first' as const) : ('second' as const),
              installments: installmentsFor(first ? 'first' : 'second'),
            },
          ]
        : [],
      splitIncomplete: false,
    };
  }, [
    charges,
    state.splitPaymentEnabled,
    state.firstCardId,
    state.secondCardId,
    state.cardsById,
    state.amounts,
    state.total,
    state.cvv,
    state.secondCvv,
    state.selectedInstallmentId,
    state.secondInstallmentId,
  ]);

  const paywayStructuralKey = useMemo(() => structuralChargesKey(preCharges), [preCharges]);
  const giftCardsKey = state.giftCards.map((card) => card.id).join('|');

  const selectedMethodId = state.paymentSelection?.selectedPaymentMethodId;
  const selectedMethod = state.paymentMethods.find(
    (item) => paymentMethodItemId(item) === selectedMethodId,
  );
  const selectedIsMercadoPago = selectedMethod?.kind === 'mercadopago';

  const selectedFirstInstallmentMonths = useMemo(() => {
    const plan = state.installmentPlans.find((item) => item.id === state.selectedInstallmentId);
    return plan?.months ?? 1;
  }, [state.installmentPlans, state.selectedInstallmentId]);

  const applyInstallmentPromoScenarioToState = useCallback((months: number) => {
    const dual = installmentPromoPreviewRef.current;
    if (!dual) {
      return false;
    }
    const scenario = installmentPromoScenarioForMonths(dual, months);
    dispatch({ type: 'LOAD_BACKEND_TOTALS', payload: scenario.totals });
    dispatch({ type: 'LOAD_BACKEND_DISCOUNT_SUMMARY', payload: scenario.discountSummary });
    const backendTotal = scenario.totals?.total;
    dispatch({
      type: 'LOAD_BACKEND_TOTAL',
      payload: typeof backendTotal === 'number' && backendTotal > 0 ? backendTotal : undefined,
    });
    return true;
  }, []);

  const applyRoutingPreviewToState = useCallback(
    (response: Parameters<typeof mapPreviewResponse>[0], requestId: number) => {
      if (requestId !== preInitiateRequestRef.current) {
        return;
      }
      installmentPromoPreviewRef.current =
        parseInstallmentPromoPreview(response.installmentPromoPreview) ?? null;
      const mapped = mapPreviewResponse(response, preCharges);
      if (mapped.missingPlans) {
        dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'error' });
        return;
      }
      routingInstallmentPlansRef.current = {
        first: mapped.firstPlans,
        second: mapped.secondPlans,
      };
      financingReferenceOneCuotaRef.current = {
        first: mapped.firstPlans.find((plan) => plan.months === 1)?.totalAmount,
        second: mapped.secondPlans.find((plan) => plan.months === 1)?.totalAmount,
      };
      dispatch({
        type: 'LOAD_INSTALLMENT_PLANS',
        payload: { first: mapped.firstPlans, second: mapped.secondPlans },
      });
      const scenarioMonths =
        mapped.firstPlans.find((plan) => plan.id === selectedInstallmentIdRef.current)?.months ??
        mapped.firstPlans.find((plan) => plan.months === 1)?.months ??
        1;
      if (!applyInstallmentPromoScenarioToState(scenarioMonths)) {
        dispatch({ type: 'LOAD_BACKEND_TOTAL', payload: mapped.backendTotal });
        dispatch({ type: 'LOAD_BACKEND_TOTALS', payload: mapped.totals });
        dispatch({ type: 'LOAD_BACKEND_DISCOUNT_SUMMARY', payload: mapped.discountSummary });
      }
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
      dispatch({ type: 'CLEAR_ERROR' });
      dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'success' });
      lastPreInitiatedStructuralKeyRef.current = paywayStructuralKey;
      lastSyncedInstallmentsKeyRef.current = resolveInstallmentsSelectionKey(
        mapped.firstPlans,
        mapped.secondPlans,
        selectedInstallmentIdRef.current,
        secondInstallmentIdRef.current,
      );
    },
    [applyInstallmentPromoScenarioToState, preCharges, paywayStructuralKey],
  );

  const handleInstallmentPromoSyncFailure = useCallback((error: unknown, requestId: number) => {
    if (requestId !== installmentPromoSyncRequestRef.current) {
      return;
    }
    if (error instanceof PaymentRequestError && error.failure === 'cancelled') {
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
      return;
    }
    if (import.meta.env.DEV) {
      const detail =
        error instanceof PaymentRequestError
          ? { status: error.status, detail: error.detail, failure: error.failure }
          : error;
      console.warn('[installment-promo/sync] failed', detail);
    }
    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: true });
    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
  }, []);

  const applyPromoSyncToState = useCallback(
    (response: Parameters<typeof mapPreviewResponse>[0], requestId: number) => {
      if (requestId !== installmentPromoSyncRequestRef.current) {
        return;
      }
      const mapped = mapPreviewResponse(response, preCharges);
      const routing = routingInstallmentPlansRef.current;
      const firstPlans = mergeInstallmentPlansForPromoSync(
        routing.first,
        mapped.firstPlans,
        selectedInstallmentIdRef.current,
      );
      const secondPlans = mergeInstallmentPlansForPromoSync(
        routing.second,
        mapped.secondPlans,
        secondInstallmentIdRef.current,
      );
      if (!installmentPromoPreviewRef.current) {
        financingReferenceOneCuotaRef.current = {
          first:
            mapped.firstPlans.find((plan) => plan.months === 1)?.totalAmount ??
            financingReferenceOneCuotaRef.current.first,
          second:
            mapped.secondPlans.find((plan) => plan.months === 1)?.totalAmount ??
            financingReferenceOneCuotaRef.current.second,
        };
      }
      if (
        preCharges.some(({ slot }) => (slot === 'first' ? firstPlans : secondPlans).length === 0)
      ) {
        dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
        const routingHadPlans =
          routing.first.length > 0 || routing.second.length > 0;
        if (routingHadPlans) {
          dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: true });
        } else {
          dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'error' });
        }
        return;
      }
      dispatch({
        type: 'LOAD_INSTALLMENT_PLANS',
        payload: { first: firstPlans, second: secondPlans },
      });
      const dualPreview = installmentPromoPreviewRef.current;
      if (dualPreview) {
        const months =
          firstPlans.find((plan) => plan.id === selectedInstallmentIdRef.current)?.months ??
          firstPlans.find((plan) => plan.months === 1)?.months ??
          1;
        applyInstallmentPromoScenarioToState(months);
      } else {
        dispatch({ type: 'LOAD_BACKEND_TOTAL', payload: mapped.backendTotal });
        dispatch({ type: 'LOAD_BACKEND_TOTALS', payload: mapped.totals });
        dispatch({ type: 'LOAD_BACKEND_DISCOUNT_SUMMARY', payload: mapped.discountSummary });
      }
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: false });
      dispatch({ type: 'CLEAR_ERROR' });
      lastSyncedInstallmentsKeyRef.current = resolveInstallmentsSelectionKey(
        firstPlans,
        secondPlans,
        selectedInstallmentIdRef.current,
        secondInstallmentIdRef.current,
      );
    },
    [applyInstallmentPromoScenarioToState, preCharges],
  );

  const handlePreviewFailure = useCallback((error: unknown, requestId: number, label: string) => {
    if (requestId !== preInitiateRequestRef.current) {
      return;
    }
    if (error instanceof PaymentRequestError && error.failure === 'cancelled') {
      return;
    }
    if (import.meta.env.DEV) {
      const detail =
        error instanceof PaymentRequestError
          ? { status: error.status, detail: error.detail, failure: error.failure }
          : error;
      console.warn(`[${label}] failed`, detail);
    }
    dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'error' });
    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
  }, []);

  // pre-initiate: routing + cache cuando cambian tarjeta, montos o gift cards (no solo la cuota).
  useEffect(() => {
    const client = clientRef.current;
    const requestId = ++preInitiateRequestRef.current;

    abortInFlightInstallmentPromoSync();
    abortInFlightPreInitiate();

    if (!client || preCharges.length === 0 || selectedIsMercadoPago) {
      dispatch({ type: 'LOAD_INSTALLMENT_PLANS', payload: { first: [], second: [] } });
      dispatch({ type: 'LOAD_BACKEND_TOTAL', payload: undefined });
      dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'idle' });
      dispatch({ type: 'LOAD_BACKEND_TOTALS', payload: undefined });
      dispatch({ type: 'LOAD_BACKEND_DISCOUNT_SUMMARY', payload: undefined });
      lastPreInitiatedStructuralKeyRef.current = '';
      lastSyncedInstallmentsKeyRef.current = '';
      routingInstallmentPlansRef.current = { first: [], second: [] };
      financingReferenceOneCuotaRef.current = {};
      installmentPromoPreviewRef.current = null;
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: false });
      return;
    }

    abortInFlightMercadoPagoPreInitiate();
    dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'loading' });
    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: false });

    const controller = new AbortController();
    preInitiateAbortRef.current = controller;

    runPreInitiateRouting(client, preCharges, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        applyRoutingPreviewToState(response, requestId);
      })
      .catch((error) => handlePreviewFailure(error, requestId, 'pre-initiate'))
      .finally(() => {
        if (preInitiateAbortRef.current === controller) {
          preInitiateAbortRef.current = null;
        }
      });

    return () => {
      abortInFlightPreInitiate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paywayStructuralKey, giftCardsKey, selectedIsMercadoPago, preInitiateRetryNonce]);

  // Con `installmentPromoPreview` del back, totales/desglose al instante al cambiar cuota (sin esperar sync).
  useEffect(() => {
    if (state.preInitiateStatus !== 'success' || !installmentPromoPreviewRef.current) {
      return;
    }
    applyInstallmentPromoScenarioToState(selectedFirstInstallmentMonths);
  }, [
    applyInstallmentPromoScenarioToState,
    selectedFirstInstallmentMonths,
    state.preInitiateStatus,
  ]);

  // Grilla: `pre-initiate` con cuota 1 (routing). Al cambiar cuota, `installment-promo/sync` alinea tag VTEX
  // (también al volver a 1 cuota tras multi). Con dual preview, el resumen se actualiza al instante y sync confirma en VTEX.
  useEffect(() => {
    const client = clientRef.current;
    if (splitIncomplete || !client || preCharges.length === 0 || selectedIsMercadoPago) {
      return;
    }
    if (state.preInitiateStatus !== 'success') {
      return;
    }
    if (lastPreInitiatedStructuralKeyRef.current !== paywayStructuralKey) {
      return;
    }

    const installmentsKey = resolveInstallmentsSelectionKey(
      state.installmentPlans,
      state.secondInstallmentPlans,
      state.selectedInstallmentId,
      state.secondInstallmentId,
    );
    if (installmentsKey === lastSyncedInstallmentsKeyRef.current) {
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: false });
      return;
    }

    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: true });
    dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: false });
    if (installmentPromoPreviewRef.current) {
      applyInstallmentPromoScenarioToState(selectedFirstInstallmentMonths);
    }

    abortInFlightInstallmentPromoSync();
    const controller = new AbortController();
    installmentPromoSyncAbortRef.current = controller;
    const requestId = ++installmentPromoSyncRequestRef.current;

    runInstallmentPromoSync(client, preCharges, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          if (requestId === installmentPromoSyncRequestRef.current) {
            dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_PENDING', payload: false });
          }
          return;
        }
        applyPromoSyncToState(response, requestId);
      })
      .catch((error) => handleInstallmentPromoSyncFailure(error, requestId))
      .finally(() => {
        if (installmentPromoSyncAbortRef.current === controller) {
          installmentPromoSyncAbortRef.current = null;
        }
      });

    return () => {
      abortInFlightInstallmentPromoSync();
    };
  }, [
    abortInFlightInstallmentPromoSync,
    applyInstallmentPromoScenarioToState,
    applyPromoSyncToState,
    handleInstallmentPromoSyncFailure,
    paywayStructuralKey,
    preCharges,
    selectedFirstInstallmentMonths,
    selectedIsMercadoPago,
    splitIncomplete,
    state.installmentPlans,
    state.preInitiateStatus,
    state.secondInstallmentId,
    state.secondInstallmentPlans,
    state.selectedInstallmentId,
    installmentPromoSyncRetryNonce,
  ]);

  const retryInstallmentsFetch = useCallback(() => {
    if (state.preInitiateStatus === 'error') {
      dispatch({ type: 'SET_PRE_INITIATE_STATUS', payload: 'loading' });
      setPreInitiateRetryNonce((nonce) => nonce + 1);
      return;
    }
    if (state.installmentPromoSyncFailed) {
      dispatch({ type: 'SET_INSTALLMENT_PROMO_SYNC_FAILED', payload: false });
      lastSyncedInstallmentsKeyRef.current = '';
      setInstallmentPromoSyncRetryNonce((nonce) => nonce + 1);
    }
  }, [state.installmentPromoSyncFailed, state.preInitiateStatus]);

  const installmentsReady = useMemo(() => {
    if (selectedIsMercadoPago) return isMercadoPagoSubmitReady(mercadoPagoPreInitiate);
    if (state.preInitiateStatus === 'loading' || state.preInitiateStatus === 'error') {
      return false;
    }
    if (!isCardId(selectedMethodId) && !state.splitPaymentEnabled) return false;
    if (state.splitPaymentEnabled) {
      return (
        state.preInitiateStatus === 'success' &&
        !state.installmentPromoSyncPending &&
        !state.installmentPromoSyncFailed &&
        state.installmentPlans.length > 0 &&
        state.secondInstallmentPlans.length > 0 &&
        !!state.selectedInstallmentId &&
        !!state.secondInstallmentId
      );
    }
    return (
      state.preInitiateStatus === 'success' &&
      !state.installmentPromoSyncPending &&
      !state.installmentPromoSyncFailed &&
      state.installmentPlans.length > 0 &&
      !!state.selectedInstallmentId
    );
  }, [
    selectedIsMercadoPago,
    mercadoPagoPreInitiate,
    selectedMethodId,
    state.preInitiateStatus,
    state.installmentPromoSyncPending,
    state.installmentPromoSyncFailed,
    state.splitPaymentEnabled,
    state.installmentPlans.length,
    state.secondInstallmentPlans.length,
    state.selectedInstallmentId,
    state.secondInstallmentId,
  ]);

  const cvvReady = useMemo(() => {
    if (selectedIsMercadoPago) return true;
    if (state.splitPaymentEnabled) {
      const first = state.firstCardId ? state.cardsById[state.firstCardId] : undefined;
      const second = state.secondCardId ? state.cardsById[state.secondCardId] : undefined;
      const firstOk = !first || !needsCvv(first) || cvvIsComplete(state.cvv);
      const secondOk = !second || !needsCvv(second) || cvvIsComplete(state.secondCvv);
      return firstOk && secondOk;
    }
    if (!isCardId(selectedMethodId)) return false;
    const card = state.cardsById[selectedMethodId];
    if (!card) return false;
    return !needsCvv(card) || cvvIsComplete(state.cvv);
  }, [
    selectedIsMercadoPago,
    selectedMethodId,
    state.splitPaymentEnabled,
    state.firstCardId,
    state.secondCardId,
    state.cardsById,
    state.cvv,
    state.secondCvv,
  ]);

  const canSubmit =
    !!selectedMethodId &&
    splitPaymentValidation.isValid &&
    !state.loading &&
    !state.loadingMethods &&
    state.paymentMethodsReady &&
    installmentsReady &&
    cvvReady &&
    state.total > 0;

  const installmentsErrorMessage =
    state.preInitiateStatus === 'error'
      ? translate('paymentsUI.errorBox.preInitiateError')
      : state.installmentPromoSyncFailed
        ? translate('paymentsUI.installment.syncFailed')
        : undefined;
  const installmentsLoading = state.preInitiateStatus === 'loading';
  const installmentsFetchFailed =
    state.preInitiateStatus === 'error' || state.installmentPromoSyncFailed;

  useEffect(() => {
    if (!isDesktop) return;
    window.parent.postMessage(
      { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'can-submit', canSubmit },
      '*',
    );
  }, [canSubmit]);

  // Mercado Pago (ENA-1607, experimento): al enfocar su banner se llama a `pre-initiate` con un único instrumento
  // (proveedor, 1 cuota y el total del carrito); `initiate` (proveedor + total) se llama al finalizar la compra.
  // TODO(ENA-1607): por ahora solo se registra la respuesta (dev) para ver qué devuelve backend; falta el manejo
  // de la redirección / resultado.
  const totalRef = useRef(state.total);
  totalRef.current = state.total;
  const logMercadoPagoFailure = (step: string, error: unknown): void => {
    if (!import.meta.env.DEV) {
      return;
    }
    const detail =
      error instanceof PaymentRequestError
        ? { status: error.status, detail: error.detail, failure: error.failure }
        : error;
    console.warn(`[mercadopago] ${step} failed`, JSON.stringify(detail));
  };

  // `initiate` necesita lo que dejó `pre-initiate`: el botón espera a que termine (`isMercadoPagoSubmitReady`).
  const handleMercadoPagoPreInitiate = useCallback(async (): Promise<boolean> => {
    const client = clientRef.current;
    if (!client) {
      return false;
    }
    abortInFlightPreInitiate();
    abortInFlightMercadoPagoPreInitiate();
    const requestId = ++mercadoPagoPreInitiateRequestRef.current;
    const controller = new AbortController();
    mercadoPagoPreInitiateAbortRef.current = controller;
    const settle = (status: MercadoPagoPreInitiateStatus) => {
      // Una consulta vieja (se cambió de medio de pago mientras corría) no pisa el estado de la actual.
      if (requestId !== mercadoPagoPreInitiateRequestRef.current) {
        return;
      }
      mercadoPagoPreInitiateStatusRef.current = status;
      setMercadoPagoPreInitiate(status);
    };
    settle('loading');
    try {
      const response = await client.preInitiate(
        {
          instruments: [
            { provider: 'MERCADOPAGO', installments: 1, amount: roundMoneyAmount(totalRef.current) },
          ],
        },
        { signal: controller.signal },
      );
      if (import.meta.env.DEV) {
        console.info('[mercadopago] pre-initiate response', JSON.stringify(response));
      }
      settle('success');
      return true;
    } catch (error) {
      if (error instanceof PaymentRequestError && error.failure === 'cancelled') {
        return false;
      }
      logMercadoPagoFailure('pre-initiate', error);
      settle('error');
      return false;
    } finally {
      if (mercadoPagoPreInitiateAbortRef.current === controller) {
        mercadoPagoPreInitiateAbortRef.current = null;
      }
    }
  }, [abortInFlightPreInitiate, abortInFlightMercadoPagoPreInitiate]);

  // Al elegir Mercado Pago (también cuando viene preseleccionado) se consulta `pre-initiate` una vez por selección.
  useEffect(() => {
    if (!selectedIsMercadoPago) {
      abortInFlightMercadoPagoPreInitiate();
      mercadoPagoPreInitiateRequestRef.current += 1;
      mercadoPagoPreInitiateStatusRef.current = 'idle';
      setMercadoPagoPreInitiate('idle');
      return;
    }
    if (!state.paymentMethodsReady) {
      return;
    }
    void handleMercadoPagoPreInitiate();
    return () => {
      abortInFlightMercadoPagoPreInitiate();
    };
  }, [
    abortInFlightMercadoPagoPreInitiate,
    handleMercadoPagoPreInitiate,
    selectedIsMercadoPago,
    state.paymentMethodsReady,
  ]);

  const handleMercadoPagoInitiate = useCallback(async () => {
    const client = clientRef.current;
    if (!client || submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    dispatch({ type: 'SET_LOADING', payload: true });
    let redirecting = false;
    try {
      // Si `pre-initiate` falló (o no llegó a terminar) se reintenta antes de cobrar: sin él `initiate` no resuelve el medio.
      if (mercadoPagoPreInitiateStatusRef.current !== 'success' && !(await handleMercadoPagoPreInitiate())) {
        setPaymentErrorModal({ message: translate('paymentsUI.errorBox.preInitiateError') });
        return;
      }
      const response = await client.initiate({
        provider: 'MERCADOPAGO',
        totalAmount: roundMoneyAmount(totalRef.current),
      });
      if (import.meta.env.DEV) {
        console.info('[mercadopago] initiate response', JSON.stringify(response));
      }
      const url = extractMercadoPagoRedirectUrl(response);
      if (!url) {
        setPaymentErrorModal({ message: translate('paymentsUI.errorBox.processingError') });
        return;
      }
      redirecting = true;
      if (isDesktop) {
        // Embed desktop: Mercado Pago no se puede mostrar dentro de un iframe; el host navega la ventana principal
        // (el iframe cross-origin no puede hacerlo). Sin respuesta del host se navega desde el propio iframe.
        requestHostMercadoPagoRedirect(url, () => window.location.assign(url));
      } else {
        // App (WebView): el iframe es el documento principal.
        window.location.assign(url);
      }
      // Si la app cancela la navegación para abrir Mercado Pago fuera del WebView, esta página sigue viva:
      // se libera el botón por si el usuario vuelve sin pagar. Si la navegación sí ocurre, la página se descarga.
      window.setTimeout(() => dispatch({ type: 'SET_LOADING', payload: false }), MERCADOPAGO_REDIRECT_RELEASE_MS);
    } catch (error) {
      logMercadoPagoFailure('initiate', error);
      setPaymentErrorModal({ message: translate('paymentsUI.errorBox.processingError') });
    } finally {
      submittingRef.current = false;
      // Mientras se redirige se mantiene el estado "procesando" (evita un segundo toque).
      if (!redirecting) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [handleMercadoPagoPreInitiate]);

  // Una sola tarjeta: cuotas >1 usan el total del plan (incluye financiación). En 1 cuota el
  // `installment.total` del back puede ser neto de mercadería; `totals.total` del pre-initiate incluye envío.
  const selectedInstallmentPlan = state.installmentPlans.find(
    (plan) => plan.id === state.selectedInstallmentId,
  );
  const selectedPlanTotal = selectedInstallmentPlan?.totalAmount;
  const singleCardTotal =
    selectedInstallmentPlan != null && selectedInstallmentPlan.months > 1
      ? selectedPlanTotal
      : (state.backendTotal ?? selectedPlanTotal);

  const installmentInterest = useMemo(() => {
    const financingRef = financingReferenceOneCuotaRef.current;
    if (!state.splitPaymentEnabled) {
      return computeInstallmentFinancingInterest(state.installmentPlans, state.selectedInstallmentId, {
        fallbackBase: state.backendTotal ?? state.total,
        simulatedOneCuotaTotal: financingRef.first,
      });
    }
    return roundMoneyAmount(
      computeInstallmentFinancingInterest(state.installmentPlans, state.selectedInstallmentId, {
        fallbackBase: state.amounts.first,
        simulatedOneCuotaTotal: financingRef.first,
      }) +
        computeInstallmentFinancingInterest(
          state.secondInstallmentPlans,
          state.secondInstallmentId,
          {
            fallbackBase: state.amounts.second,
            simulatedOneCuotaTotal: financingRef.second,
          },
        ),
    );
  }, [
    state.splitPaymentEnabled,
    state.backendTotal,
    state.total,
    state.installmentPlans,
    state.secondInstallmentPlans,
    state.selectedInstallmentId,
    state.secondInstallmentId,
    state.amounts,
  ]);

  // Tasas (CFT, TEA y TNA) de las cuotas con interés elegidas, una por tarjeta y en su orden.
  const installmentRates = useMemo<PaymentHostRates[]>(() => {
    const selected = [
      state.installmentPlans.find((plan) => plan.id === state.selectedInstallmentId),
      ...(state.splitPaymentEnabled
        ? [state.secondInstallmentPlans.find((plan) => plan.id === state.secondInstallmentId)]
        : []),
    ];
    return selected.flatMap((plan) => (plan?.rates ? [plan.rates] : []));
  }, [
    state.splitPaymentEnabled,
    state.installmentPlans,
    state.secondInstallmentPlans,
    state.selectedInstallmentId,
    state.secondInstallmentId,
  ]);

  useEffect(() => {
    postPaymentHostInterest(installmentInterest, installmentRates);
  }, [installmentInterest, installmentRates]);

  useEffect(() => {
    if (readEmbedSource(window.location.search) !== 'desktop') return;
    if (state.preInitiateStatus !== 'success' || selectedIsMercadoPago) return;

    const checkoutTotal = state.splitPaymentEnabled
      ? state.total
      : (singleCardTotal ?? state.backendTotal ?? state.total);

    // El host arma descuento (solo mercadería) y total (plan + envío). No enviar `installmentPromoDiscount`
    // como `state.total - plan`: `state.total` incluye envío y el plan no, y restaba envío como descuento.
    postPaymentHostPreview({
      checkoutTotal,
      ...(state.backendTotals ? { totals: state.backendTotals } : {}),
      ...(state.backendDiscountSummary ? { discountSummary: state.backendDiscountSummary } : {}),
    });
  }, [
    state.preInitiateStatus,
    state.backendTotals,
    state.backendDiscountSummary,
    state.splitPaymentEnabled,
    state.total,
    singleCardTotal,
    state.backendTotal,
    selectedIsMercadoPago,
    state.selectedInstallmentId,
    state.secondInstallmentId,
  ]);

  const handleSubmit = useCallback(async () => {
    analytics.onSubmit();
    if (!canSubmit) {
      return;
    }
    if (selectedIsMercadoPago) {
      await handleMercadoPagoInitiate();
      return;
    }
    const client = clientRef.current;
    const services = cardServicesRef.current;
    if (!client || !services || charges.length === 0) {
      dispatch({ type: 'SET_ERROR', payload: translate('paymentsUI.errorBox.selectPaymentMethod') });
      return;
    }

    const installmentIds = [state.selectedInstallmentId, state.secondInstallmentId];
    const securityCodes = state.splitPaymentEnabled ? [state.cvv, state.secondCvv] : [state.cvv];
    // Una sola tarjeta: se cobra el `totals.total` de pre-initiate (backend valida el monto). Con pago
    // dividido los montos los define el usuario.
    // TODO(ENA-1607) [sin validar con un pago aprobado]: backend indicó usar `totals.total` de
    // `pre-initiate` como monto de `initiate`. Falta confirmar cómo se reparte con pago dividido y qué
    // pasa si el total cambia entre `pre-initiate` e `initiate`.
    const inputs: ChargeInput[] = charges.map((charge, index) => ({
      ...charge,
      // order-service valida cada monto de `initiate` contra el total del plan de cuotas elegido (con intereses):
      // una sola tarjeta manda `singleCardTotal` (total del plan elegido, o `backendTotal` si no hay plan).
      amount: !state.splitPaymentEnabled && singleCardTotal ? singleCardTotal : charge.amount,
      installments: Number(installmentIds[index]),
      securityCode: securityCodes[index],
    }));
    if (inputs.some(({ installments }) => !Number.isInteger(installments) || installments < 1)) {
      dispatch({ type: 'SET_ERROR', payload: translate('paymentsUI.errorBox.selectInstallments') });
      return;
    }

    // Un solo `initiate` en vuelo: evita cobros duplicados por doble toque.
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    dispatch({ type: 'SET_LOADING', payload: true });

    let awaitingHostRedirect = false;
    try {
      // Token de pago NUEVO por operación (tarjeta guardada: token + CVV con el SDK). Si falla acá
      // todavía no hubo cobro: es seguro reintentar.
      const prepared = await resolvePaymentTokens(services.tokenizer, inputs).then(
        (ready) => ({ ready }),
        (error: unknown) => ({ error }),
      );
      if ('error' in prepared) {
        const tokenMessage = paymentTokenErrorMessage(prepared.error);
        if (prepared.error instanceof PaymentTokenError && prepared.error.reason === 'missing-cvv') {
          // Falta el CVV: es un aviso del formulario, no un fallo del pago.
          dispatch({ type: 'SET_ERROR', payload: tokenMessage });
        } else {
          // Un solo aviso (el modal) con el motivo real, p. ej. "La tarjeta nueva venció por tiempo".
          dispatch({ type: 'CLEAR_ERROR' });
          setPaymentErrorModal({ message: tokenMessage });
        }
        return;
      }

      const usedSessionTokens = prepared.ready
        .filter(({ card }) => isSessionCard(card))
        .map(({ card }) => card.token);

      const outcome = await processPayment(client, prepared.ready);

      // El token de pago de una tarjeta nueva es de un solo uso: se descarta tras cada intento.
      if (usedSessionTokens.length > 0) {
        usedSessionTokens.forEach((token) => services.cardCache.remove(token));
        dispatch({
          type: 'LOAD_PAYMENT_METHODS',
          payload: toPaymentMethods(
            mergeCards(remoteCardsRef.current, services.cardCache.list()),
            createCardDesignResolver(), mercadoPagoEnabledRef.current,
          ),
          selectFirstCard: false,
        });
        dispatch({ type: 'CLEAR_CARD_SELECTION' });
      }

      const isConfirmedOrPending = outcome.status === 'approved' || outcome.status === 'pending';

      if (isConfirmedOrPending && outcome.orderId) {
        if (isDesktop) {
          // El checkout web muestra el éxito y redirige a VTEX orderPlaced (sin UI alta en el iframe).
          postPaymentHostApproved(outcome.orderId);
          awaitingHostRedirect = true;
        } else {
          // La app detecta esta ruta en el WebView y avanza a PaymentSuccess.
          redirectToOrderPlaced(outcome.orderId);
        }
      } else if (isConfirmedOrPending) {
        if (!isDesktop) {
          dispatch({ type: 'SET_SUCCESS', payload: outcome });
        }
      } else {
        // Si se usó una tarjeta nueva, su token (de un solo uso) ya se descartó: hay que volver a ingresarla.
        const base = outcome.message ?? translate('paymentsUI.paymentFailure.paymentDetail');
        const detail =
          usedSessionTokens.length > 0 ? `${base} ${translate('paymentsUI.errorBox.reAddCard')}` : base;
        dispatch({ type: 'CLEAR_ERROR' });
        setPaymentErrorModal({ message: detail });
      }

      if (awaitingHostRedirect) {
        return;
      }
    } finally {
      submittingRef.current = false;
      if (!awaitingHostRedirect) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [
    analytics.onSubmit,
    canSubmit,
    charges,
    state.selectedInstallmentId,
    state.secondInstallmentId,
    state.splitPaymentEnabled,
    singleCardTotal,
    selectedIsMercadoPago,
    handleMercadoPagoInitiate,
    state.cvv,
    state.secondCvv,
  ]);

  useEffect(() => {
    if (!isDesktop) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as { source?: string; type?: string; layout?: string } | null;
      if (!data || data.source !== CHECKOUT_HOST_MESSAGE_SOURCE) return;
      if (data.type === 'submit') void handleSubmit();
      if (data.type === 'layout' && (data.layout === 'mobile' || data.layout === 'desktop')) {
        setHostViewport(data.layout);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleSubmit]);

  if (orderPlaced) {
    return withPaymentClient(
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          backgroundColor: '#F5F5F5',
          boxSizing: 'border-box',
        }}
      >
        <PaymentSuccessPanel status="approved" orderId={orderPlaced.orderId} />
      </div>,
    );
  }

  if (missingSession) {
    return (
      <SessionExpired />
    );
  }

  if (state.view === 'add-card' && !isDesktop) {
    return withPaymentClient(
      <div className="min-h-screen bg-white py-6 px-4">
        <div className="w-full mx-auto">
          <AddCardForm
            onBack={handleBackToPayment}
            onSave={handleSaveNewCard}
            testID="add-card-form"
          />
        </div>
      </div>,
    );
  }

  if (state.success && state.paymentResult) {
    const panelStatus =
      state.paymentResult.status === 'pending' ? 'pending' : 'approved';
    return withPaymentClient(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: 16,
          backgroundColor: '#F5F5F5',
          boxSizing: 'border-box',
        }}
      >
        <PaymentSuccessPanel
          status={panelStatus}
          orderId={state.paymentResult.orderId}
          redirecting={isDesktop && panelStatus === 'approved'}
        />
        {!isDesktop && state.paymentResult.message ? (
          <p style={{ margin: 0, textAlign: 'center', color: '#666', maxWidth: 420 }}>
            {state.paymentResult.message}
          </p>
        ) : null}
      </div>,
    );
  }

  // Modales compartidos por la vista mobile/app y la de web desktop.
  const overlays = (
    <>
        {cardAddedModal && (
          <CardStatusModal
            variant="success"
            onClose={() => setCardAddedModal(false)}
            testID="app-card-added-modal"
          />
        )}

        {paymentErrorModal && !isDesktop && (
          <CardStatusModal
            variant="paymentError"
            message={paymentErrorModal.message}
            onClose={handleDismissPaymentFailure}
            testID="app-payment-error-modal"
          />
        )}

        {pendingDelete && (
          <ConfirmDeleteCardModal
            last4={pendingDelete.last4}
            layout={isDesktop ? 'dialog' : 'sheet'}
            loading={deleting}
            onConfirm={handleConfirmDeleteCard}
            onCancel={handleCancelDeleteCard}
            testID="app-confirm-delete-card-modal"
          />
        )}
    </>
  );

  const mockedAddress = 'Belgrano 279, San Nicolás de los Arroyos, Buenos Aires'

  if (isDesktop) {
    return withPaymentClient(
      <>
      <WebPayment
        loadingMethods={state.loadingMethods}
        paymentMethodsReady={state.paymentMethodsReady}
        paymentMethods={state.paymentMethods as PaymentMethodItem[]}
        installmentPlans={state.installmentPlans}
        secondInstallmentPlans={state.secondInstallmentPlans}
        total={state.total}
        selectedMethodId={state.paymentSelection?.selectedPaymentMethodId}
        cvv={state.cvv}
        selectedInstallmentId={state.selectedInstallmentId}
        splitPaymentEnabled={state.splitPaymentEnabled}
        firstCardId={state.firstCardId}
        secondCardId={state.secondCardId}
        firstAmount={state.amounts.first}
        secondAmount={state.amounts.second}
        secondCvv={state.secondCvv}
        secondInstallmentId={state.secondInstallmentId}
        loading={state.loading}
        error={state.error}
        canSubmit={canSubmit}
        installmentsLoading={installmentsLoading}
        installmentsErrorMessage={installmentsFetchFailed ? installmentsErrorMessage : undefined}
        onRetryInstallments={installmentsFetchFailed ? retryInstallmentsFetch : undefined}
        address={mockedAddress}
        giftCardModalOpen={state.giftCardModalOpen}
        addingCard={state.view === 'add-card'}
        onRemoveCard={handleAskDeleteCardById}
        onSaveCard={async (card) => {
          if (card) {
            await handleSaveNewCard(card);
          } else {
            dispatch({ type: 'SET_VIEW', payload: 'payment' });
          }
        }}
        onSelectMethod={(id) => {
          analytics.onUserSelect(id);
          dispatch({ type: 'UPDATE_SELECTION', payload: { selectedPaymentMethodId: id } as any });
        }}
        onCvvChange={handleCVVChange}
        onCvvError={handleCVVError}
        onSelectInstallment={(planId) => dispatch({ type: 'SELECT_INSTALLMENT', payload: planId })}
        onToggleSplit={handleToggleSplitPayment}
        onShowAddCard={handleShowAddCardForm}
        onSelectFirstCard={handleSelectFirstCard}
        onSelectSecondCard={handleSelectSecondCard}
        onFirstAmountChange={handleFirstAmountChange}
        onSecondAmountChange={handleSecondAmountChange}
        onSecondCvvChange={handleSecondCVVChange}
        onSelectSecondInstallment={(planId) =>
          dispatch({ type: 'SELECT_SECOND_INSTALLMENT', payload: planId })
        }
        onSubmit={handleSubmit}
        hostViewport={hostViewport}
        onOpenGiftCard={() => {
          window.parent.postMessage(
            { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'open-gift-card' },
            '*',
          );
        }}
        onCloseGiftCard={handleCloseGiftCardModal}
        giftCards={state.giftCards}
        onApplyGiftCard={handleApplyGiftCard}
        onRemoveGiftCard={handleRemoveGiftCard}
        paymentFailureDetail={paymentErrorModal?.message}
        onDismissPaymentFailure={
          paymentErrorModal ? handleDismissPaymentFailure : undefined
        }
      />
      {overlays}
      </>,
    );
  }

  const checkoutSteps: readonly StepperStep[] = [
    { step: 1, number: '1', label: translate('paymentsUI.checkoutStepper.cartStep') },
    { step: 2, number: '2', label: translate('paymentsUI.checkoutStepper.deliveryStep') },
    { step: 3, number: '3', label: translate('paymentsUI.checkoutStepper.paymentStep') },
  ];

  return withPaymentClient(
    <div className="min-h-screen bg-white flex flex-col">
      {/* Stepper del checkout: el iframe es dueño del suyo (la app lo oculta al mostrar el WebView). */}
      <div className="pb-6 px-4">
        <CheckoutStepper
          steps={checkoutSteps}
          activeStep={3}
          testID="app-checkout-stepper"
        />
      </div>

      {/* Contenido: ocupa el alto restante y deja el footer pegado al fondo */}
      <div className="flex-1">
      {/* Promo de código de regalo - full width debajo del stepper */}
      <div className="mb-4">
        <GiftCardPromo
          onPress={handleOpenGiftCardModal}
          testID="app-gift-card-promo"
          accessibilityLabel="¿Tenés un código de regalo?"
        />
      </div>

      <div className="w-full mx-auto px-4">

        {/* Encabezado */}
        <SelectPaymentMethodHeader
          title={translate(state.splitPaymentEnabled ? 'paymentsUI.header.title2Cards' : 'paymentsUI.header.title')}
          onAddPress={state.splitPaymentEnabled ? undefined : handleShowAddCardForm}
          testID="app-header"
        />

        {/* Carrusel de métodos de pago (oculto en modo 2 tarjetas: cada tarjeta elige la suya) */}
        {!state.splitPaymentEnabled && state.loadingMethods && (
          <div className="mb-2 pl-4">
            <PaymentMethodsSkeleton
              variant="carousel-mobile"
              carouselInset="app"
              withSelectedCardFlow
              testID="payment-methods-loading"
            />
          </div>
        )}
        {!state.splitPaymentEnabled && !state.loadingMethods && (
          <div className="mb-2 pl-4">
            <PaymentMethodCarousel
              items={state.paymentMethods}
              selectedId={state.paymentSelection?.selectedPaymentMethodId}
              onRemove={handleAskDeleteCard}
              onSelect={(item) => {
                const itemId = paymentMethodItemId(item);
                analytics.onUserSelect(itemId);
                dispatch({ type: 'UPDATE_SELECTION', payload: { selectedPaymentMethodId: itemId } });
              }}
              onAddPress={handleShowAddCardForm}
              testID="payment-carousel"
            />
          </div>
        )}

        {/* Input CVV (tarjeta guardada, modo 1 tarjeta). Las tarjetas nuevas de la sesión ya llevan
            el CVV dentro de su token de pago. */}
        {!state.splitPaymentEnabled &&
          isCardId(state.paymentSelection?.selectedPaymentMethodId) &&
          needsCvv(state.cardsById[state.paymentSelection?.selectedPaymentMethodId ?? '']) && (
          <div className="mb-2">
            <CVVInput
              value={state.cvv}
              onChange={handleCVVChange}
              onError={handleCVVError}
              testID="cvv-input"
            />
          </div>
        )}

        {/* Botón Pagar con 2 tarjetas (modo 1 tarjeta) */}
        {!state.splitPaymentEnabled && isCardId(state.paymentSelection?.selectedPaymentMethodId) && (
          <div>
            <TogglePaymentCardsButton
              mode="add"
              onPress={handleToggleSplitPayment}
              testID="toggle-payment-cards-button"
            />
          </div>
        )}

        {/* Selector de cuotas (modo 1 tarjeta) */}
        {!state.splitPaymentEnabled && isCardId(state.paymentSelection?.selectedPaymentMethodId) && (
          <div className="mb-4">
            <InstallmentSelector
              plans={state.installmentPlans}
              selectedId={state.selectedInstallmentId || ''}
              onSelect={(planId: string) => dispatch({ type: 'SELECT_INSTALLMENT', payload: planId })}
              // Sin este prop el selector mostraba "No pudimos obtener las cuotas" mientras `pre-initiate` cargaba.
              loading={installmentsLoading || state.preInitiateStatus === 'idle'}
              errorMessage={installmentsFetchFailed ? installmentsErrorMessage : undefined}
              onRetry={installmentsFetchFailed ? retryInstallmentsFetch : undefined}
              testID="installment-selector"
            />
          </div>
        )}

        {/* Pago con 2 tarjetas: carrusel propio + monto + CVV + cuotas, por cada tarjeta */}
        {state.splitPaymentEnabled && (
          <>
            <div className="mb-8">
              <SplitPaymentCardSection
                highlightSelection={false}
                onRemoveCard={handleAskDeleteCard}
                cardLabel={translate('paymentsUI.splitPayment.firstCardLabel')}
                cardOptions={cardOptions}
                selectedCardId={state.firstCardId}
                onSelectCard={handleSelectFirstCard}
                amount={state.amounts.first}
                onAmountChange={handleFirstAmountChange}
                showCvv={needsCvv(state.firstCardId ? state.cardsById[state.firstCardId] : undefined)}
                cvv={state.cvv}
                onCVVChange={handleCVVChange}
                onCVVError={handleCVVError}
                installmentTitle={translate('paymentsUI.splitPayment.installmentSectionTitleFirst')}
                installmentsPending={!splitReady}
                installmentPlans={state.installmentPlans}
                selectedInstallmentId={state.selectedInstallmentId}
                onSelectInstallment={(planId) => dispatch({ type: 'SELECT_INSTALLMENT', payload: planId })}
                testID="split-payment-first-card"
              />
            </div>
            <div className="mb-8">
              <TogglePaymentCardsButton
                mode="remove"
                onPress={handleToggleSplitPayment}
                testID="toggle-payment-cards-button"
              />
            </div>
            <div className="mb-4">
              <SplitSectionDivider />
            </div>
            <div className="mb-8">
              <SplitPaymentCardSection
                onAddPress={handleShowAddCardForm}
                addLabel={translate('paymentsUI.web.add')}
                onRemoveCard={handleAskDeleteCard}
                cardLabel={translate('paymentsUI.splitPayment.secondCardLabel')}
                cardOptions={cardOptions.filter(
                  (item) => item.kind !== 'card' || item.id !== state.firstCardId,
                )}
                selectedCardId={state.secondCardId}
                onSelectCard={handleSelectSecondCard}
                amount={state.amounts.second}
                onAmountChange={handleSecondAmountChange}
                showCvv={needsCvv(state.secondCardId ? state.cardsById[state.secondCardId] : undefined)}
                cvv={state.secondCvv}
                onCVVChange={handleSecondCVVChange}
                onCVVError={handleCVVError}
                installmentTitle={translate('paymentsUI.splitPayment.installmentSectionTitleSecond')}
                installmentsPending={!splitReady}
                installmentPlans={state.secondInstallmentPlans}
                selectedInstallmentId={state.secondInstallmentId}
                onSelectInstallment={(planId) => dispatch({ type: 'SELECT_SECOND_INSTALLMENT', payload: planId })}
                testID="split-payment-second-card"
              />
            </div>
          </>
        )}

        {/* Información de dirección */}
        {state.paymentSelection?.selectedPaymentMethodId && (
          <div className="mb-8">
            <AddressInfoBox
              address={mockedAddress}
              testID="address-info"
              title={translate('paymentsUI.addressInfo.cardBillingAddressTitle')}
            />
          </div>
        )}

        {/* Errores */}
        {state.error && (
          <div className="mb-6">
            <PaymentErrorBox
              message={state.error}
              testID="payment-error"
              accessibilityLabel={translate('paymentsUI.errorBox.ariaLabel', { message: state.error })}
            />
          </div>
        )}

        {/* Spacer para que el footer sticky no tape contenido */}
        {state.paymentSelection?.selectedPaymentMethodId && (
          <div className="h-32" />
        )}
      </div>

      </div>

      {/* Total + botón submit - sticky footer */}
      {state.paymentSelection?.selectedPaymentMethodId && (
        <CheckoutTotalFooter
          total={state.splitPaymentEnabled ? state.total : (singleCardTotal ?? state.total)}
          onSubmit={handleSubmit}
          disabled={!splitPaymentValidation.isValid}
          loading={state.loading}
          // "Ver detalle" (resumen del pedido) oculto en mobile. Para volver a mostrarlo:
          // summary={state.splitPaymentEnabled ? undefined : state.backendTotals}
          testID="checkout-total-footer"
        />
      )}

      {overlays}

      {/* Gift Card Promo Modal */}
      <GiftCardPromoModal
        isOpen={state.giftCardModalOpen}
        onClose={handleCloseGiftCardModal}
        giftCards={state.giftCards}
        onApply={handleApplyGiftCard}
        onRemove={handleRemoveGiftCard}
        testID="app-gift-card-modal"
      />
    </div>,
  );
};

export default App;
